/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useRepositories } from '@data/data.providers';
import { UserPreferencesService } from '@domain/services/user-preferences.service';
import { OrdersService } from '@domain/services/orders.service';
import { InvoicesService } from '@domain/services/invoices.service';
import { ShipmentsService } from '@domain/services/shipments.service';
import { CasesService } from '@domain/services/cases.service';
import { ProductionAssignmentsService } from '@domain/services/production-assignments.service';
import { ClientPortalService } from '@domain/services/client-portal.service';
import { EmployeePortalService } from '@domain/services/employee-portal.service';
import { DeliveryNotesService } from '@domain/services/delivery-notes.service';
import { ClientsService } from '@domain/services/clients.service';
import { PatientsService } from '@domain/services/patients.service';
import { WorkpieceTypesService } from '@domain/services/workpiece-types.service';
import { ResourcesService } from '@domain/services/resources.service';
import { EmployeesService } from '@domain/services/employees.service';
import { LabRoutesService } from '@domain/services/lab-routes.service';
import { ProductionPartnersService } from '@domain/services/production-partners.service';
import { DesignRequestsService } from '@domain/services/design-requests.service';
import { OrderChatService } from '@domain/services/order-chat.service';

/**
 * Exposes every domain service the UI consumes. Services are plain
 * classes that accept their repository dependencies via the
 * constructor and are built once per mount from the
 * `RepositoriesProvider` value.
 */
export interface Services {
  readonly userPreferencesService: UserPreferencesService;
  readonly ordersService: OrdersService;
  readonly invoicesService: InvoicesService;
  readonly shipmentsService: ShipmentsService;
  readonly casesService: CasesService;
  readonly productionAssignmentsService: ProductionAssignmentsService;
  readonly clientPortalService: ClientPortalService;
  readonly employeePortalService: EmployeePortalService;
  readonly deliveryNotesService: DeliveryNotesService;
  readonly clientsService: ClientsService;
  readonly patientsService: PatientsService;
  readonly workpieceTypesService: WorkpieceTypesService;
  readonly resourcesService: ResourcesService;
  readonly employeesService: EmployeesService;
  readonly labRoutesService: LabRoutesService;
  readonly productionPartnersService: ProductionPartnersService;
  readonly designRequestsService: DesignRequestsService;
  readonly orderChatService: OrderChatService;
}

const ServicesContext = createContext<Services | null>(null);

type Props = {
  readonly children: ReactNode;
  readonly overrides?: Partial<Services>;
};

export function ServicesProvider({ children, overrides }: Props): JSX.Element {
  const repos = useRepositories();

  const services = useMemo<Services>(
    () => ({
      userPreferencesService: new UserPreferencesService(repos.userPreferencesRepository),
      ordersService: new OrdersService(repos.orderRepository),
      invoicesService: new InvoicesService(repos.invoiceRepository),
      shipmentsService: new ShipmentsService(repos.shipmentRepository),
      casesService: new CasesService(repos.caseRepository),
      productionAssignmentsService: new ProductionAssignmentsService(
        repos.productionAssignmentRepository,
      ),
      clientPortalService: new ClientPortalService(repos.clientPortalRepository),
      employeePortalService: new EmployeePortalService(repos.employeePortalRepository),
      deliveryNotesService: new DeliveryNotesService(repos.deliveryNoteRepository),
      clientsService: new ClientsService(repos.clientRepository),
      patientsService: new PatientsService(repos.patientRepository),
      workpieceTypesService: new WorkpieceTypesService(repos.workpieceTypeRepository),
      resourcesService: new ResourcesService(repos.resourceRepository),
      employeesService: new EmployeesService(repos.employeeRepository),
      labRoutesService: new LabRoutesService(repos.labRouteRepository),
      productionPartnersService: new ProductionPartnersService(repos.productionPartnerRepository),
      designRequestsService: new DesignRequestsService(repos.designRequestRepository),
      orderChatService: new OrderChatService(repos.orderChatRepository),
      ...overrides,
    }),
    [repos, overrides],
  );

  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}

export function useServices(): Services {
  const ctx = useContext(ServicesContext);
  if (!ctx) {
    throw new Error('useServices must be used within a ServicesProvider');
  }
  return ctx;
}
